import { useMemo } from 'react';
// 🛰️ Updated import to point to the unified shared types
import { AppState, CalendarEvent as EngineEvent, CustomCalendar, DataScope } from '@/types';
import { parseISO, areIntervalsOverlapping, isValid } from 'date-fns';

/**
 * 🛰️ MASTER CALENDAR STATE HOOK
 * Refactored for Hybrid Scoping: Merges Personal Partition and Household Hub data.
 * Adheres to Google Data Privacy standards via strict ownership filtering.
 */

export const useCalendarState = (
  personalEvents: EngineEvent[],
  householdEvents: EngineEvent[],
  customEvents: EngineEvent[], // 🎯 These are now treated as actual events, not just definitions
  activeCalendarIds: string[],
  currentUserId: string,
  currentHouseholdId: string | null,
  activeMembers: string[],
  activeScope: DataScope
) => {

  // Helper to ensure valid Date objects for date-fns math
  const toDate = (dateInput: any): Date => {
    if (dateInput instanceof Date) return dateInput;
    return typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
  };

  // --- 1. SECURE DATA MERGE & HYBRID FILTERING ---
  const mergedEvents = useMemo(() => {
    // Combine all potential EngineEvents from all sources
    const allRawEngineEvents = [...personalEvents, ...householdEvents, ...customEvents];

    // Filter events based on active calendar IDs and scope
    const filteredEvents = allRawEngineEvents.filter((e: EngineEvent) => {
      const isPersonalCalendarActive = activeCalendarIds.includes('personal');
      const isHouseholdCalendarActive = activeCalendarIds.includes('household');
      const isEventFromActiveCustomCalendar = e.customCalendarId && activeCalendarIds.includes(e.customCalendarId);

      // Rule 1: Check if the event's associated calendar is active
      let isAssociatedCalendarActive: boolean = false;
      if (e.customCalendarId) {
        isAssociatedCalendarActive = !!isEventFromActiveCustomCalendar; // Ensure it's a boolean
      } else if (e.source === 'personal') {
        isAssociatedCalendarActive = isPersonalCalendarActive;
      } else if (e.source === 'household') {
        isAssociatedCalendarActive = isHouseholdCalendarActive;
      }

      // If the event's calendar isn't active (or is a custom event whose custom calendar isn't active), don't include it.
      if (!isAssociatedCalendarActive) return false;

      // Rule 2: Check if the event's source and ownership match the active scope
      let includeByScopeAndOwnership = false;

      if (activeScope === 'PERSONAL') {
        // Only include personal events owned by the current user
        includeByScopeAndOwnership = (e.source === 'personal' && e.ownerId === currentUserId);
      } else if (activeScope === 'SHARED') {
        // Only include household events if a household is selected
        includeByScopeAndOwnership = (e.source === 'household' && currentHouseholdId !== null);
      } else if (activeScope === 'BOTH') {
        // Include personal events owned by current user OR household events if a household is selected
        includeByScopeAndOwnership = (e.source === 'personal' && e.ownerId === currentUserId) ||
                                    (e.source === 'household' && currentHouseholdId !== null);
      }

      // If an event is a custom event from an active custom calendar, and it meets
      // either the personal ownership or household membership criteria, it should be included.
      // This ensures custom events are not double-filtered away if their source/owner aligns with the active scope.
      if (isEventFromActiveCustomCalendar) {
        if ((e.source === 'personal' && e.ownerId === currentUserId) ||
            (e.source === 'household' && currentHouseholdId !== null)) {
          includeByScopeAndOwnership = true; // Override previous scope check if this custom event is relevant
        }
      }

      return includeByScopeAndOwnership;
    });

    // --- 🧬 CHRONOLOGICAL UNIFICATION ---
    return filteredEvents.sort((a: EngineEvent, b: EngineEvent) => {
      const timeA = toDate(a.start).getTime();
      const timeB = toDate(b.start).getTime();
      return timeA - timeB;
    });
  }, [personalEvents, householdEvents, customEvents, activeCalendarIds, currentUserId, currentHouseholdId, activeMembers, activeScope]);;
  // --- 2. CONFLICT RADAR LOGIC ---
  const hasConflicts = useMemo(() => {
    // Privacy Logic: Conflicts are only alerted for the current user's perspective
    // to prevent leaking data about other members' private schedules.
    const radarEvents = mergedEvents.filter(e =>
      e.ownerId === currentUserId || (e.source === 'household' && currentHouseholdId) // Consider household events only if a household is active
    );

    for (let i = 0; i < radarEvents.length; i++) {
      for (let j = i + 1; j < radarEvents.length; j++) {
        const eventA = radarEvents[i];
        const eventB = radarEvents[j];

        // Extract base ID to handle sliced events (e.g., 'eventid-slice-0' -> 'eventid')
        const getBaseId = (e: EngineEvent) => e.id.split('-slice-')[0];

        // If both events are slices of the same original event, they don't conflict with each other
        if (getBaseId(eventA) === getBaseId(eventB)) {
          continue; // Skip conflict check for slices of the same logical event
        }

        const startA = toDate(eventA.start);
        const endA = toDate(eventA.end);
        const startB = toDate(eventB.start);
        const endB = toDate(eventB.end);

        if (isValid(startA) && isValid(endA) && isValid(startB) && isValid(endB)) {
          if (areIntervalsOverlapping({ start: startA, end: endA }, { start: startB, end: endB })) {
            return true;
          }
        }
      }
    }
    return false;
  }, [mergedEvents, currentUserId, currentHouseholdId]);

  // --- 3. MEMBER PERSPECTIVE GROUPING ---
  const eventsByMember = useMemo(() => {
    const groups: Record<string, EngineEvent[]> = {}; // Use EngineEvent[] consistently

    // If no household, only the current user column exists
    const membersToProcess = currentHouseholdId ? activeMembers : [currentUserId];

    (membersToProcess || []).forEach((mId: string) => {
      groups[mId] = mergedEvents.filter((event: EngineEvent) => { // Use EngineEvent consistently
        // Shared events visible to everyone in the grid if a household is active
        if (event.source === 'household' && currentHouseholdId) return true;
        // Personal events are visible only in their owner's column
        return event.ownerId === mId;
      });
    });

    return groups;
  }, [mergedEvents, activeMembers, currentUserId, currentHouseholdId]);

  return {
    events: mergedEvents,
    eventsByMember,
    hasConflicts,
    totalVisibleCount: mergedEvents.length,
    activeScope // Returning scope for UI context awareness
  };
};